/**
 * @file 编辑报名选项
 */

import { Button } from '@seiue/ui'
import { pick } from '@seiue/util'
import { Checkbox, Form, FormProps, Input, InputNumber } from 'antd'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { RichTextEditor } from 'packages/components/RichText'
import { OptionReq } from 'packages/sdks-next/growp'
import { EntryComponentsWrapper } from 'packages/theme'

/**
 * 编辑/新增 选项弹窗内容
 *
 * @param param - 参数
 * @param param.hide - 关闭弹窗
 * @param param.setOptions - 设置当前选项方法
 * @param param.options - 当前所有选项
 * @param param.option - 传入需要编辑的选项，如为空则说明当前是新增
 * @param param.optionIndex - 传入需要编辑的选项索引，如为空则说明当前是新增
 * @param param.outForm - 外部表单
 * @returns 组件
 */
export const EditOpenContent: React.FC<{
  hide: () => void
  setOptions: (op: OptionReq[]) => void
  options: OptionReq[]
  option?: OptionReq
  optionIndex?: number
  outForm?: FormProps['form']
}> = ({ hide, setOptions, options, option, optionIndex, outForm }) => {
  const [form] = Form.useForm()
  let initialValues = {}
  if (option) {
    initialValues = {
      name: option.name,
      description: option.description,
      volumes:
        option.volume && option.volume > 0
          ? {
              volumeNumber: option.volume,
            }
          : {},
      volume: option.volume,
    }
  }
  const [checkVolume, setCheckVolume] = useState<boolean | undefined>(
    option?.volume && option?.volume > 0
      ? false
      : option?.volume == 0
      ? true
      : undefined
  )
  const [required, setRequired] = useState(true)
  useEffect(() => {
    if (checkVolume !== undefined) {
      setRequired(!checkVolume)
    }
  }, [checkVolume])

  const onSave = () => {
    form.submit()

    form.validateFields().then((values) => {
      const volumes = values.volumes
      const addOption = {
        ...pick(values, ['name', 'volume', 'description']),
        volume: volumes.noLimit === true ? 0 : volumes.volumeNumber ?? 0,
        ...(option === undefined
          ? {}
          : pick(option, ['id', 'schoolId', 'activityTaskId'])),
      }
      if (optionIndex === undefined) {
        const saveOps = [...options, addOption]
        setOptions(saveOps)
        if (outForm !== undefined && options) {
          outForm.setFieldValue('options', saveOps)
        }
      } else {
        options[optionIndex] = addOption
        setOptions([...options])
        if (outForm !== undefined && options) {
          outForm.setFieldValue('options', options)
        }
      }
      outForm?.validateFields(['options'])

      hide()
    })
  }

  return (
    <EntryComponentsWrapper>
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        style={{
          padding: '20px 20px 0 20px',
          height: '450px',
          overflow: 'scroll',
        }}
      >
        <Form.Item
          label="选项标题"
          name="name"
          rules={[{ required: true, message: '请输入选项标题' }]}
        >
          <Input
            placeholder="请输入选项标题"
            maxLength={200}
          />
        </Form.Item>
        <Form.Item
          label="选项容量"
          required={true}
          name="volume"
          rules={[
            {
              required: required,
              message: '请设置选项容量',
            },
          ]}
        >
          <InputBox>
            <Form.Item
              name={['volumes', 'volumeNumber']}
              style={{
                display: 'inline-block',
              }}
            >
              <InputNumber
                placeholder="请输入"
                min={1}
                max={10000}
                onBlur={(e) => {
                  form.setFieldValue('volume', e.target.value)
                }}
                disabled={!!checkVolume}
              />
            </Form.Item>
            <Form.Item
              name={['volumes', 'noLimit']}
              style={{
                marginLeft: '12px',
                display: 'inline-block',
              }}
            >
              <Checkbox
                checked={!!checkVolume}
                onChange={(e) => {
                  setCheckVolume(e.target.checked)
                  if (e.target.checked) {
                    form.setFieldValue(['volumes', 'volumeNumber'], undefined)
                  }
                  form.setFieldValue(['volumes', 'noLimit'], e.target.checked)
                }}
              >
                不限
              </Checkbox>
            </Form.Item>
          </InputBox>
        </Form.Item>
        <Form.Item
          label="选项简介"
          name="description"
        >
          <RichTextEditor
            minHeight={160}
            useFullScreen={false}
          />
        </Form.Item>
      </Form>
      <FooterBox>
        <BtnBox>
          <Button onClick={() => hide()}>取消</Button>
          <Button
            type="primary"
            onClick={onSave}
          >
            确定
          </Button>
        </BtnBox>
      </FooterBox>
    </EntryComponentsWrapper>
  )
}

const FooterBox = styled.div`
  border-top: 1px solid ${(props) => props.theme.border.greyNormal};
  height: 56px;
  display: flex;
  justify-content: right;
  padding: 12px 20px;
`

const BtnBox = styled.div``

const InputBox = styled.div`
  height: 30px;
`
